import React, { useState, useEffect } from "react";
import { IoArrowBack, IoSearch, IoHandLeftOutline } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import { IoCart, IoHomeOutline, IoHeartOutline, IoGridOutline, IoPersonOutline } from "react-icons/io5";

const HomeScreen = () => {
	const [searchActive, setSearchActive] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const location = useLocation();
	const navigate = useNavigate();
	const [selectedCategory, setSelectedCategory] = useState("All");
	const loginSuccess = location.state?.loginSuccess;

	const categories = [
		"All",
		"Baby Care",
		"Fashion & Beauty",
		"Personal Care",
		"Electronics & Gadgets",
		"Phones & Accessories",
	];

	const heroImages = [
		"/BUY iT Students Hero Section.png",
		"https://images.unsplash.com/photo-1505740420928-5e560c06d30e?q=80&w=1200&auto=format&fit=crop",
		"https://images.unsplash.com/photo-1523275335684-37898b6baf30?q=80&w=1200&auto=format&fit=crop",
		"https://images.unsplash.com/photo-1513708927688-890f9f20b7e8?q=80&w=1200&auto=format&fit=crop",
	];

	const [products, setProducts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [personalCareProducts, setPersonalCareProducts] = useState([]);
	const [personalCareLoading, setPersonalCareLoading] = useState(true);
	const [personalCareError, setPersonalCareError] = useState(null);
	const [fashionBeautyProducts, setFashionBeautyProducts] = useState([]);
	const [fashionBeautyLoading, setFashionBeautyLoading] = useState(true);
	const [fashionBeautyError, setFashionBeautyError] = useState(null);
	const [cartMessage, setCartMessage] = useState("");
	const [loginSuccessMessage, setLoginSuccessMessage] = useState("");
	const [showWhatsAppPrompt, setShowWhatsAppPrompt] = useState(false);

	useEffect(() => {
		if (loginSuccess) {
			setLoginSuccessMessage(loginSuccess);
			setTimeout(() => setLoginSuccessMessage(""), 2000);
		}
		const fetchPersonalCareProducts = async () => {
			try {
				setPersonalCareLoading(true);
				setPersonalCareError(null);
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/products/category?category=Personal%20Cares&limit=10`);
				if (!response.ok) throw new Error("Failed to fetch personal care products");
				const data = await response.json();
				setPersonalCareProducts(Array.isArray(data?.data) ? data.data : []);
			} catch (error) {
				setPersonalCareError("Error fetching personal care products.");
				setPersonalCareProducts([]);
			} finally {
				setPersonalCareLoading(false);
			}
		};
		const fetchFashionBeautyProducts = async () => {
			try {
				setFashionBeautyLoading(true);
				setFashionBeautyError(null);
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/products/fashion-beauty?limit=10`);
				if (!response.ok) throw new Error("Failed to fetch fashion and beauty products");
				const data = await response.json();
				setFashionBeautyProducts(Array.isArray(data?.data) ? data.data : []);
			} catch (error) {
				setFashionBeautyError("Error fetching fashion & beauty products.");
				setFashionBeautyProducts([]);
			} finally {
				setFashionBeautyLoading(false);
			}
		};
		const fetchProduct = async () => {
			try {
				setLoading(true);
				setError(null);
				// Always use latest token for user-specific products
				const token = localStorage.getItem("userToken");
				const headers = { "Content-Type": "application/json" };
				if (token) headers["Authorization"] = `Bearer ${token}`;
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/products/public`, {
					credentials: "include",
					headers,
				});
				if (!response.ok) throw new Error("Failed to fetch products");
				const data = await response.json();
				setProducts(data);
			} catch (error) {
				setError("Error fetching products.");
				setProducts([]);
			} finally {
				setLoading(false);
			}
		};
		fetchPersonalCareProducts();
		fetchFashionBeautyProducts();
		fetchProduct();
	}, [loginSuccess]);

	const [cartCounts, setCartCounts] = useState({});
	// Fetch cart from backend on mount
	useEffect(() => {
		const fetchCart = async () => {
			try {
				const token = localStorage.getItem("userToken");
				const headers = { "Content-Type": "application/json" };
				if (token) headers["Authorization"] = `Bearer ${token}`;
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart`, {
					credentials: "include",
					headers,
				});
				if (response.status === 200) {
					const data = await response.json();
					const counts = {};
					if (data && data.data && Array.isArray(data.data.items)) {
						data.data.items.forEach(item => {
							counts[item.productId] = item.quantity;
						});
					}
					setCartCounts(counts);
				} else {
					setCartCounts({});
				}
			} catch (err) {
				setCartCounts({});
			}
		};
		fetchCart();
	}, [loginSuccessMessage]);

	useEffect(() => {
		const handleScroll = () => {
			setShowWhatsAppPrompt(window.scrollY > 900);
		};

		handleScroll();
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// When products change, keep cartCounts for products in cart, but don't reset
	const handleAdd = async (id) => {
		try {
			const token = localStorage.getItem("userToken");
			const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart/add`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify({ productId: id, quantity: 1 }),
				credentials: "include"
			});
			const data = await response.json();
			const messageText = (data && data.message ? data.message : "").toLowerCase();
			const shouldRedirectToLogin =
				response.status === 401 ||
				messageText.includes("login") ||
				messageText.includes("access cart") ||
				messageText.includes("unauthorized");
			if (data.success) {
				setCartMessage(data.message);
				setTimeout(() => setCartMessage(""), 2000);
				setCartCounts((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
			} else {
				setCartMessage(data.message || "Failed to add product to cart.");
				setTimeout(() => setCartMessage(""), 2000);
				if (shouldRedirectToLogin) {
					setTimeout(() => {
						navigate("/login", {
							state: { loginMessage: data.message || "Login to access cart" }
						});
					}, 300);
				}
			}
		} catch (err) {
			setCartMessage("Failed to add product to cart.");
			setTimeout(() => setCartMessage(""), 2000);
		}
	};

	const handleRemove = (id) => {
		setCartCounts((prev) => {
			const newCounts = { ...prev };
			if (newCounts[id] > 1) {
				newCounts[id] = newCounts[id] - 1;
			} else {
				delete newCounts[id];
			}
			return newCounts;
		});
	};

	return (
		<div className="min-h-screen bg-[#F1F3F2] text-gray-900">
			<style>
				{`
					.no-scrollbar::-webkit-scrollbar { display: none; }
					.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
					@keyframes hero-scroll {
						0%, 20% { transform: translateX(0); }
						25%, 45% { transform: translateX(-100%); }
						50%, 70% { transform: translateX(-200%); }
						75%, 95% { transform: translateX(-300%); }
						100% { transform: translateX(-400%); }
					}
					@keyframes soft-shake {
						0%, 100% { transform: translate3d(0, 0, 0); }
						20% { transform: translate3d(-2px, 0, 0) rotate(-1deg); }
						40% { transform: translate3d(2px, 0, 0) rotate(1deg); }
						60% { transform: translate3d(-1px, 0, 0) rotate(-0.5deg); }
						80% { transform: translate3d(1px, 0, 0) rotate(0.5deg); }
					}
				`}
			</style>

			{/* Cart success message */}
			{loginSuccessMessage && (
				<div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-[#A3E7C1] text-black px-6 py-3 rounded-lg shadow-lg text-sm font-semibold animate-fadeInOut" style={{ minWidth: '220px', textAlign: 'center', letterSpacing: '0.5px' }}>
					{loginSuccessMessage}
				</div>
			)}
			{cartMessage && (
				<div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-[#19C463] text-white px-6 py-3 rounded-lg shadow-lg text-sm font-semibold animate-fadeInOut" style={{ minWidth: '220px', textAlign: 'center', letterSpacing: '0.5px', top: loginSuccessMessage ? '60px' : '16px' }}>
					{cartMessage}
				</div>
			)}
			{showWhatsAppPrompt && (
				<button
					type="button"
					onClick={() => window.open("https://wa.me/2349121440680", "_blank", "noopener,noreferrer")}
					className="fixed right-3 z-30 flex max-w-[170px] items-center gap-2 border border-[#19C463]/25 bg-white px-3 py-2 text-left text-sm text-gray-900 transition hover:bg-[#f9fffb]"
					style={{
						bottom: "96px",
						borderRadius: "10px",
						boxShadow: "0 6px 18px rgba(0, 0, 0, 0.12)",
						animation: "soft-shake 2.6s ease-in-out infinite",
					}}
				>
					<span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-[#19C463] text-white">
						<IoHandLeftOutline className="h-4 w-4" />
					</span>
					<span className="leading-[1.2]">
						Need something else? Check here
					</span>
				</button>
			)}

			<div className="sticky top-0 z-20 bg-white">
				<style>
					{`
						@keyframes fadeInOut {
							0% { opacity: 0; }
							10% { opacity: 1; }
							90% { opacity: 1; }
							100% { opacity: 0; }
						}
						.animate-fadeInOut {
							animation: fadeInOut 2s;
						}
					`}
				</style>
				<div className="px-4 pt-4 pb-4">
					<div className="flex items-center gap-3">
						<img
							src="/BUY%20iT%20Logo.png"
							alt="BUY iT logo"
							className="h-10 w-10 object-contain"
						/>
						<div className="flex-1">
							{!searchActive ? (
								<div className="relative">
									<input
										type="text"
										placeholder="Search products"
										className="w-full bg-white py-2 text-sm focus:outline-none transition-all duration-300"
										style={{ borderRadius: "8px", paddingLeft: "12px", paddingRight: "8px", border: "2px solid #000000", width: "100%" }}
										onFocus={() => setSearchActive(true)}
									/>
								</div>
							) : (
								<div className="flex items-center gap-2 bg-white py-2 px-2 rounded-lg shadow-sm transition-all duration-300" style={{ width: "100%", border: "2px solid #000000" }}>
									<IoArrowBack className="h-5 w-5 text-gray-700 cursor-pointer" onClick={() => { setSearchActive(false); setSearchValue(""); }} />
									<input
										type="text"
										value={searchValue}
										onChange={e => setSearchValue(e.target.value)}
										placeholder="Search products"
										className="flex-1 bg-white text-sm focus:outline-none"
										style={{ border: "none", outline: "none", paddingLeft: "8px" }}
										autoFocus
									/>
									<IoSearch className="h-5 w-5 text-gray-700 cursor-pointer" onClick={() => {
										if (searchValue.trim()) {
											window.location.href = `/search?q=${encodeURIComponent(searchValue.trim())}`;
										}
									}} />
								</div>
							)}
						</div>
						<div className="relative">
							{/* Show total quantity of all products in cart, only if cart is not empty */}
							{Object.keys(cartCounts).length > 0 && (
								<span className="absolute -right-1 -top-1 h-5 min-w-[20px] rounded-full bg-[#19C463] px-1 text-xs text-white flex items-center justify-center">
									{Object.keys(cartCounts).length}
								</span>
							)}
							<button
								type="button"
								className="h-10 w-10 bg-white flex items-center justify-center"
								aria-label="Cart"
								onClick={() => navigate("/cart")}
								style={{
									borderRadius: "4px",
									boxShadow: "0 0 4px rgba(0, 0, 0, 0.25)"
								}}
							>
								<svg
									viewBox="0 0 24 24"
									className="h-5 w-5 text-gray-700"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<circle cx="9" cy="21" r="1" />
									<circle cx="20" cy="21" r="1" />
									<path d="M1 1h4l2.5 13h11.5l2-8H6" />
								</svg>
							</button>
						</div>
					</div>
				</div>

				<div className="px-4 pb-4">
					<div className="no-scrollbar flex gap-3 overflow-x-auto">
						{categories.map((category) => (
							<button
								key={category}
								type="button"
								onClick={() => setSelectedCategory(category)}
								className={`whitespace-nowrap px-1 py-1 text-sm transition ${
									selectedCategory === category
										? "bg-[#A3E7C1] border border-[#19C463] text-[#000000]"
										: "border border-[#000000] text-gray-700 hover:border-[#19C463] hover:text-[#19C463]"
								}`}
								style={{ borderRadius: "4px", borderWidth: "1px" }}
							>
								{category}
							</button>
						))}
					</div>
				</div>
			</div>

			<div className="mt-4 flex flex-col gap-3">
				<div>
					<div className="relative overflow-hidden bg-white px-[10px] py-[11px]">
						<div
							className="flex gap-2 w-full"
							style={{
								width: "100%",
								animation: "hero-scroll 16s ease-in-out infinite",
							}}
						>
							{[...heroImages, ...heroImages].map((src, index) => (
								<div
									key={`${src}-${index}`}
									className="h-full w-full flex-shrink-0 bg-white rounded-[24px] overflow-hidden flex items-center justify-center"
								>
									<img
										src={src}
										alt="Promotion"
										className={`w-full ${
											src === "/BUY iT Students Hero Section.png"
												? "h-[210px] object-contain"
												: "h-[220px] object-cover"
										}`}
									/>
								</div>
							))}
						</div>
					</div>
				</div>

				<div className="px-4 py-4" style={{ backgroundColor: "#EAD0D6" }}>
					<div className="flex items-center justify-between">
						<h2 className="text-sm font-semibold text-gray-900">Your Glow Starts Here</h2>
						<button
							type="button"
							onClick={() => navigate(`/category/${encodeURIComponent("Personal Cares")}`)}
							className="text-xs text-gray-500"
						>
							Discover All
						</button>
					</div>
					<div className="no-scrollbar mt-3 flex gap-3 overflow-x-auto">
						{personalCareLoading ? (
							<div className="text-sm text-gray-500">Loading products...</div>
						) : personalCareError ? (
							<div className="text-sm text-red-500">{personalCareError}</div>
						) : !Array.isArray(personalCareProducts) || personalCareProducts.length === 0 ? (
							<div className="text-sm text-gray-500">No products found.</div>
						) : (
							(personalCareProducts || []).map((product) => (
								<div
									key={`top-${product._id}`}
									className="min-w-[150px] max-w-[150px] rounded-lg border border-gray-100 bg-white shadow-sm overflow-hidden"
								>
									<div
										className="aspect-[4/3] w-full overflow-hidden rounded-md cursor-pointer"
										onClick={() => navigate(`/product/${product._id}`)}
									>
									<img
										loading="lazy"
										src={product.productImages && product.productImages[0] ? product.productImages[0] : "/placeholder.png"}
										alt={product.productName}
										className="h-full w-full object-cover"
									/>
									</div>
									<div className="mt-2 text-left p-1">
										<p className="text-xs font-semibold text-gray-900 truncate">
											{product.productName}
										</p>
										<p className="text-xs text-gray-700">₦ {Number(product.productSellingPrice || 0).toLocaleString("en-NG")}</p>
										<p className="text-[11px] text-gray-500">
											{typeof product.productQuantity === "number" ? `${product.productQuantity} left` : "Stock"}
										</p>
									</div>
								</div>
							))
						)}
					</div>
				</div>

				<div className="px-4 py-4" style={{ backgroundColor: "#EAD0D6" }}>
					<div className="flex items-center justify-between">
						<h2 className="text-sm font-semibold text-gray-900">Style & Beauty Picks</h2>
						<button
							type="button"
							onClick={() => navigate(`/category/${encodeURIComponent("Fashion & Beauty")}`)}
							className="text-xs text-gray-500"
						>
							Discover All
						</button>
					</div>
					<div className="no-scrollbar mt-3 flex gap-3 overflow-x-auto">
						{fashionBeautyLoading ? (
							<div className="text-sm text-gray-500">Loading products...</div>
						) : fashionBeautyError ? (
							<div className="text-sm text-red-500">{fashionBeautyError}</div>
						) : !Array.isArray(fashionBeautyProducts) || fashionBeautyProducts.length === 0 ? (
							<div className="text-sm text-gray-500">No products found.</div>
						) : (
							(fashionBeautyProducts || []).map((product) => (
								<div
									key={`fashion-${product._id}`}
									className="min-w-[150px] max-w-[150px] rounded-lg border border-gray-100 bg-white shadow-sm overflow-hidden"
								>
									<div
										className="aspect-[4/3] w-full overflow-hidden rounded-md cursor-pointer"
										onClick={() => navigate(`/product/${product._id}`)}
									>
									<img
										loading="lazy"
										src={product.productImages && product.productImages[0] ? product.productImages[0] : "/placeholder.png"}
										alt={product.productName}
										className="h-full w-full object-cover"
									/>
									</div>
									<div className="mt-2 text-left p-1">
										<p className="text-xs font-semibold text-gray-900 truncate">
											{product.productName}
										</p>
										<p className="text-xs text-gray-700">₦ {Number(product.productSellingPrice || 0).toLocaleString("en-NG")}</p>
										<p className="text-[11px] text-gray-500">
											{typeof product.productQuantity === "number" ? `${product.productQuantity} left` : "Stock"}
										</p>
									</div>
								</div>
							))
						)}
					</div>
				</div>

				<div className="bg-white pb-24">
					<div className="grid grid-cols-2 gap-x-[2px] gap-y-[6px]">
						{loading ? (
							<div className="col-span-2 text-center py-8 text-gray-500">Loading products...</div>
						) : error ? (
							<div className="col-span-2 flex flex-col items-center justify-center gap-3 py-8 text-center">
								<p className="text-sm text-gray-700">Connection lost. Let's get you back online.</p>
								<button
									type="button"
									onClick={() => window.location.reload()}
									className="px-4 py-2 text-sm font-semibold"
									style={{ backgroundColor: "#19C463", color: "#ffffff", borderRadius: "6px" }}
								>
									Reload
								</button>
							</div>
						) : !Array.isArray(products) || products.length === 0 ? (
							<div className="col-span-2 text-center py-8 text-gray-500">No products found.</div>
						) : (
							(products || []).map((product) => {
								return (
									<div
										key={product._id}
										className="rounded-lg p-1 flex flex-col gap-1"
									>
										<div className="rounded-t-[4px] aspect-[10/9] w-full overflow-hidden cursor-pointer"
											onClick={() => navigate(`/product/${product._id}`)}>
											<img
												loading="lazy"
												src={product.productImages && product.productImages[0] ? product.productImages[0] : '/placeholder.png'}
												alt={product.productName}
												className="h-full w-full object-cover"
											/>
										</div>
										<div className="mt-3 text-left cursor-pointer"
											onClick={() => navigate(`/product/${product._id}`)}>
											<p className="text-sm font-medium text-gray-900 truncate">
												{product.productName} - {product.productDescription}
											</p>
											{product.productQuantity <= 5 && (
												<span className="mt-1 inline-block text-xs font-bold text-[#19C463] tracking-wider" style={{ textTransform: 'uppercase' }}>
													ONLY 1 LEFT
												</span>
											)}
											<div className="mt-2 flex items-center gap-2">
												<span className="text-sm font-semibold text-gray-900">
													₦ {Number(product.productSellingPrice || 0).toLocaleString("en-NG")}
												</span>
												<span className="text-xs text-gray-500">{product.productSold}</span>
											</div>
										</div>

										<div className="mt-3">
											{cartCounts[product._id] > 0 ? (
												<div className="flex items-center justify-between px-2 py-1">
													<button
														type="button"
														onClick={() => handleRemove(product._id)}
														className="h-8 w-8 bg-[#19C463] text-white"
														aria-label="Decrease quantity"
														style={{ borderRadius: "4px" }}
													>
														-
													</button>
													<span className="text-sm font-bold text-black">{cartCounts[product._id]}</span>
													<button
														type="button"
														onClick={() => handleAdd(product._id)}
														className="h-8 w-8 bg-[#19C463] text-white"
														aria-label="Increase quantity"
														style={{ borderRadius: "4px" }}
													>
														+
													</button>
												</div>
											) : (
												<button
													type="button"
													onClick={() => handleAdd(product._id)}
													className="w-full flex items-center justify-center gap-2 rounded bg-[#19C463] px-1 py-2.5 text-sm font-medium text-white transition"
													style={{ borderRadius: "4px" }}
												>
													Add to cart
													<IoCart className="h-4 w-4" />
												</button>
											)}
										</div>
									</div>
								);
							})
						)}
					</div>
				</div>
			</div>

			<nav className="fixed bottom-0 inset-x-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.08)]">
				<div className="flex items-center justify-around py-3">
					<button
						type="button"
						onClick={() => navigate("/home")}
						className="flex flex-col items-center gap-1"
						style={{ color: location.pathname === "/home" ? "#19C463" : "#000000" }}
					>
						<IoHomeOutline className="h-6 w-6" />
						<span className="p-1 text-sm">Home</span>
					</button>
					<button
						type="button"
						className="flex flex-col items-center gap-1"
						style={{ color: "#000000" }}
					>
						<IoHeartOutline className="h-6 w-6" />
						<span className="p-1 text-sm">Wishlist</span>
					</button>
					<button
						type="button"
						onClick={() => navigate("/category")}
						className="flex flex-col items-center gap-1"
						style={{ color: location.pathname === "/category" ? "#19C463" : "#000000" }}
					>
						<IoGridOutline className="h-6 w-6" />
						<span className="p-1 text-sm">Category</span>
					</button>
					<button
						type="button"
						onClick={() => navigate("/profile")}
						className="flex flex-col items-center gap-1"
						style={{ color: location.pathname === "/profile" ? "#19C463" : "#000000" }}
					>
						<IoPersonOutline className="h-6 w-6" />
						<span className="p-1 text-sm">Profile</span>
					</button>
				</div>
			</nav>
		</div>
	);
};

export default HomeScreen;


